import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ExternalLink, Eye, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import useActiveRegions from "@/features/regions/hooks/use-active-regions";
import {
  CopyCohort,
  CopyContext,
  CopyIssue,
  hasCopyTags,
  listCopyCohorts,
  previewCopy,
} from "../services/copy-tags-api";
import {
  cohortLabel,
  livePageUrl,
  pickCohortAsOf,
  renderLines,
} from "../utils/copy-preview";

// Shows the finished copy before saving, as a customer in a chosen pricing
// cohort reads it. One cohort per release date of the pack's price and its
// sessions' reschedule fees — there can be any number of them. `getValue`
// returns the copy as it would be saved, or throws with a message (e.g.
// invalid JSON).
export default function CopyPreview({
  context,
  getValue,
  allRegionsWhenUnset,
  paymentPageSlug,
}: {
  context: CopyContext;
  getValue: () => unknown;
  // Region-less copy (booking copy) with no region chosen: preview it once per
  // active region, each with its own figures.
  allRegionsWhenUnset?: boolean;
  // A saved payment page: offers to open the live page as the selected cohort.
  paymentPageSlug?: string | null;
}) {
  const [asOf, setAsOf] = useState<string | null>(null);
  const [results, setResults] = useState<
    { region: string | null; result: PreviewResult }[] | null
  >(null);
  const [loading, setLoading] = useState(false);
  const regions = useActiveRegions().data ?? [];

  const ctx = context;
  const perRegion = !!allRegionsWhenUnset && !ctx.regionId;
  // A changed region / product / cohort makes the shown preview stale.
  useEffect(() => {
    setResults(null);
  }, [ctx.regionId, ctx.productId, ctx.bundleStandardId]);
  const { data: cohorts = [] } = useQuery({
    queryKey: [
      "copy-cohorts",
      ctx.bundleStandardId ?? null,
      ctx.regionId ?? null,
      ctx.managedEventId ?? null,
      ctx.productId ?? null,
    ],
    queryFn: () => listCopyCohorts(ctx),
    staleTime: 1000 * 60,
  });

  // Default to the newest cohort — what a customer joining today reads.
  useEffect(() => {
    const next = pickCohortAsOf(cohorts, asOf);
    if (next !== asOf && next) setAsOf(next);
  }, [cohorts, asOf]);

  const run = async (anchor: string | null = asOf) => {
    if (!anchor) return;
    let value: unknown;
    try {
      value = getValue();
    } catch (e: any) {
      setResults([{ region: null, result: { error: e.message } }]);
      return;
    }
    if (!hasCopyTags(value)) {
      setResults([{ region: null, result: { value, issues: [] } }]);
      return;
    }
    const targets = perRegion
      ? regions.map((r) => ({ id: r.id, name: r.display_name }))
      : [{ id: ctx.regionId ?? null, name: null }];
    setLoading(true);
    try {
      setResults(
        await Promise.all(
          targets.map(async (t) => ({
            region: t.name,
            result: await previewCopy({ ...ctx, regionId: t.id }, value, anchor)
              .then((r): PreviewResult => r)
              .catch(
                (e: any): PreviewResult => ({
                  error: e.response?.data?.message || "Preview failed",
                }),
              ),
          })),
        ),
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rounded-md border border-dashed border-gray-300 p-3 space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <Eye className="h-4 w-4 text-gray-500" />
        <span className="text-xs text-gray-600 mr-auto">
          Preview what a customer reads
        </span>
        <Select
          value={asOf ?? undefined}
          onValueChange={(v) => {
            setAsOf(v);
            run(v);
          }}
        >
          <SelectTrigger className="h-auto min-h-7 w-80 text-xs text-left">
            <SelectValue placeholder="Cohort" />
          </SelectTrigger>
          <SelectContent>
            {cohorts.map((c) => (
              <SelectItem key={c.asOf} value={c.asOf} className="text-xs">
                <div className="flex flex-col">
                  <span>{cohortLabel(c, cohorts)}</span>
                  {c.changes.length > 0 && (
                    <span className="text-[10px] text-gray-400">
                      New: {c.changes.join(" · ")}
                    </span>
                  )}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-7 px-2 text-xs"
          disabled={loading || !asOf}
          onClick={() => run()}
        >
          <RefreshCw className="h-3.5 w-3.5 mr-1" /> Preview
        </Button>
        {paymentPageSlug && (
          <Button
            type="button"
            size="sm"
            className="h-7 px-2 text-xs bg-orange-500 hover:bg-orange-600 text-white"
            disabled={!asOf}
            title="Opens the saved page as a customer in the selected cohort — save first to see your edits."
            onClick={() =>
              window.open(
                livePageUrl(paymentPageSlug, asOf ?? ""),
                "_blank",
                "noopener",
              )
            }
          >
            <ExternalLink className="h-3.5 w-3.5 mr-1" /> Open payment page
          </Button>
        )}
      </div>

      {loading && <p className="text-xs text-gray-400">Rendering…</p>}
      {results?.map(({ region, result }) => (
        <div key={region ?? "single"} className="space-y-1">
          {region && (
            <p className="text-[11px] font-medium text-gray-500 pt-1">{region}</p>
          )}
          <PreviewBody result={result} />
        </div>
      ))}
    </div>
  );
}

type PreviewResult = { value?: unknown; issues?: CopyIssue[]; error?: string };

function PreviewBody({ result }: { result: PreviewResult }) {
  if (result.error) {
    return <p className="text-xs text-red-600">{result.error}</p>;
  }
  if (result.issues?.length) {
    return (
      <ul className="text-xs text-red-600 list-disc pl-4">
        {result.issues.map((i) => (
          <li key={i.tag + i.message}>
            <code>{i.tag}</code> — {i.message}
          </li>
        ))}
      </ul>
    );
  }
  return (
    <div className="text-sm text-gray-800 space-y-1">
      {renderLines(result.value).map((line, idx) => (
        <p key={idx}>{line}</p>
      ))}
    </div>
  );
}
